import { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen } from 'lucide-react';

/**
 * ReportGlossary — collapsible footer with the abbreviations and scoring
 * scales that appear across PFD analytics reports. Customers (and especially
 * non-technical farm managers) repeatedly asked "what does FCR / SGR / BFCR /
 * ABW mean?" and "what's the 0–8 in Stock Integrity Score?" — this puts the
 * answers in one place at the bottom of every report instead of scattered
 * tooltips.
 *
 * Defaults open=false so it doesn't take screen real estate; one click reveals
 * everything. Renders inline (no portal) so PDF exports include it.
 */
const TERMS = [
  {
    term: 'FCR',
    full: 'Feed Conversion Ratio',
    body: 'Total feed given (kg) ÷ total weight gained (kg). Lower is more efficient — but a low FCR alongside a missing-fish signal can mean unrecorded mortality (the fewer fish ate the same feed) rather than real efficiency.',
  },
  {
    term: 'BFCR',
    full: 'Biological FCR',
    body: 'FCR calculated against the biomass actually produced (start biomass + growth − mortality), excluding feed lost to harvested or dead fish. The most accurate efficiency metric for a closed cycle.',
  },
  {
    term: 'SGR',
    full: 'Specific Growth Rate',
    body: '% body-weight gain per day. Computed as ln(W_end / W_start) ÷ days × 100. Standard species tables map SGR to weight × temperature.',
  },
  {
    term: 'SFR',
    full: 'Specific Feeding Rate',
    body: '% of body weight fed per day. Looked up from species tables at the current weight × temperature. Used to compute Recommended Feed = biomass × SFR%.',
  },
  {
    term: 'ABW',
    full: 'Average Body Weight',
    body: 'Mean individual fish weight (g) at a sampling event. Same as "avg weight" / "average weight" labels elsewhere.',
  },
  {
    term: 'Stock Integrity Score',
    full: '0–8 scale',
    body: 'Composite signal of stock-count integrity from a snapshot. 0–2 = strong red flag (likely missing fish), 3–5 = moderate / inconclusive, 6–8 = clean. Always treat single-snapshot scores as indicative — confirm with a follow-up sample.',
  },
  {
    term: 'Loss Window',
    full: 'amber band on chart',
    body: 'A date range where the model and reconstructed actual-growth lines decoupled most strongly. The most probable interval during which a biomass/fish-count loss occurred.',
  },
  {
    term: 'Incorrect-Sample Flag (▲)',
    full: 'red dashed marker',
    body: 'A specific sample whose recorded weight cannot be supported by the surrounding feed and growth evidence. Re-check the field notes, scale calibration, and mortality logs at that date.',
  },
  {
    term: 'Magnitude',
    full: 'Small / Medium / Large',
    body: 'Size of the FCR deviation from the model. NOT a confidence rating — a single snapshot can show a Large magnitude and still be Indicative-only in reliability. See Stock Integrity Score for the reliability axis.',
  },
  {
    term: 'Indicative',
    full: 'reliability tier',
    body: 'Single-snapshot or low-data findings. Acts as an early signal, not a verdict. Confirm with follow-up sampling before operational decisions.',
  },
];

export default function ReportGlossary({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="mt-6 border border-slate-200 rounded-lg bg-slate-50/60 print:break-inside-avoid"
      data-testid="report-glossary"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-100 transition-colors rounded-t-lg"
        data-testid="report-glossary-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <BookOpen className="w-4 h-4 text-slate-500" />
          Glossary &amp; Scoring Scales
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {open && (
        <div className="p-3 pt-0">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {TERMS.map((t) => (
              <div key={t.term} className="border-l-2 border-slate-300 pl-3">
                <dt className="text-sm">
                  <span className="font-semibold text-slate-800">{t.term}</span>
                  <span className="ml-1 text-xs text-slate-500">— {t.full}</span>
                </dt>
                <dd className="text-xs text-slate-600 leading-relaxed mt-0.5">{t.body}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-[11px] text-slate-500 italic">
            All metrics are computed from the inputs you provided + species reference tables. Single-snapshot readings (Snapshot Analysis) carry an "Indicative" reliability — schedule a follow-up sample before acting on operational findings.
          </p>
        </div>
      )}
    </div>
  );
}
